import * as React from "react";
import {
  CellTemplate,
  Cell,
  Compatible,
  Uncertain,
  getCellProperty,
  UncertainCompatible,
} from "@silevis/reactgrid";
import { VsaaTag } from "../../models/VsaaTag";
import { VsaaTagPicker } from "../tagpicker/VsaaTagPicker";
import { DisplayField } from "../DisplayField";
import { FieldTypes } from "../../models/FieldTypes";
import { useState } from "react";
import { ProcessFieldUniqueName } from "../../constants/Fields";

export interface TagCell extends Cell {
  type: "tags";
  text: string;
  tagValue: TagCellValue;
}

export interface TagCellValue {
  fieldName: string;
  label: string;
  tagCollectionId: string;
  multiValue: boolean;
  tags: VsaaTag[];
}

export const TagCellTemplate = (
  usedTagsId?: string[]
): CellTemplate<TagCell> => {
  // Allows us to track when the edit dialog is closed or open.
  // This is practically to properly trigger rerender when the dialog is closed without any changes.
  const [shouldOpenDialogOpen, setShouldOpenDialog] = useState<boolean>(false);

  const handleKeyDown = (
    cell: Compatible<TagCell>,
    _keyCode: number, // Avoid using since it is deprecated.
    _ctrl: boolean,
    _shift: boolean,
    _alt: boolean,
    key?: string
  ) => {
    const enableEdit = key === "Enter" || key === "DoubleClick";

    setShouldOpenDialog(enableEdit);

    return {
      cell: cell,
      enableEditMode: enableEdit,
    };
  };

  const getCompatibleCell = (
    uncertainCell: Uncertain<TagCell>
  ): Compatible<TagCell> => {
    const text = getCellProperty(uncertainCell, "text", "string");
    const tagCellValue: TagCellValue = JSON.parse(text);

    if (!tagCellValue.label || !tagCellValue.tagCollectionId) {
      throw Error("Failed to parse tag cell");
    }

    return { ...uncertainCell, text, tagValue: tagCellValue, value: NaN };
  };

  const onPickerChange = (
    newTags: VsaaTag[],
    cell: Compatible<TagCell>,
    onCellChanged: (cell: Compatible<TagCell>, commit: boolean) => void
  ) => {
    const newTagValue: TagCellValue = {
      fieldName: cell.tagValue.fieldName,
      label: cell.tagValue.label,
      tags: newTags,
      tagCollectionId: cell.tagValue.tagCollectionId,
      multiValue: cell.tagValue.multiValue,
    };
    const newTextValue = JSON.stringify(newTagValue);

    cell.tagValue = newTagValue;
    cell.text = newTextValue;

    onCellChanged(cell, true);
  };

  const getClearedTagValueText = (oldTagValue: TagCellValue) => {
    const clearedValue: TagCellValue = oldTagValue;
    clearedValue.tags = [];

    return JSON.stringify(clearedValue);
  };

  const onPickerClose = (
    cell: Compatible<TagCell>,
    onCellChanged: (cell: Compatible<TagCell>, commit: boolean) => void
  ) => {
    onCellChanged(cell, true);
    setShouldOpenDialog(false);
  };

  const update = (
    cell: Compatible<TagCell>,
    cellToMerge: UncertainCompatible<TagCell>
  ): Compatible<TagCell> => {
    // Backspace clears a cell and in those cases text is empty. So this is to enable backspace clearing of cells.
    const cellText = cellToMerge.text
      ? cellToMerge.text
      : getClearedTagValueText(cell.tagValue);

    return getCompatibleCell({ ...cell, text: cellText });
  };

  const render = (
    cell: Compatible<TagCell>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<TagCell>, commit: boolean) => void
  ): React.ReactNode => {
    return isInEditMode && shouldOpenDialogOpen ? (
      <VsaaTagPicker
        multiValue={cell.tagValue.multiValue}
        hideDisplay
        value={cell.tagValue.tags}
        label={cell.tagValue.label}
        tagCollectionId={cell.tagValue.tagCollectionId}
        onChange={(newTags) => onPickerChange(newTags, cell, onCellChanged)}
        onClose={() => onPickerClose(cell, onCellChanged)}
        usedTagsId={usedTagsId}
        disableRootNodes={cell.tagValue.fieldName === ProcessFieldUniqueName}
      />
    ) : (
      <DisplayField
        type={FieldTypes.Tag}
        label={cell.tagValue.label}
        value={cell.tagValue.tags}
        disableTextWrap
      />
    );
  };

  return { getCompatibleCell, render, update, handleKeyDown };
};
